import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

export default function Practice() {
  const [subject, setSubject] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [q, setQ] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [count, setCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);

  useEffect(() => { api.get("/subjects").then((r) => setSubjects(r.data)); }, []);

  const next = async () => {
    setLoading(true); setResult(null); setSelected(null);
    try {
      const { data } = await api.post("/practice/next", { subject });
      setQ(data);
    } catch (e) {
      toast.error("Could not fetch question");
    } finally { setLoading(false); }
  };

  const submit = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await api.post("/practice/attempt", { question_id: q.question_id, answer: selected });
      setResult(data);
      setCount((c) => c + 1);
      if (data.correct) setCorrectCount((c) => c + 1);
      if (data.new_badges?.length) {
        confetti({ particleCount: 140, spread: 90, origin: { y: 0.6 }, colors: ["#FF5E5B", "#FFD166", "#06D6A0", "#4F98E8"] });
        for (const b of data.new_badges) {
          toast.success(`${b.emoji} Badge unlocked: ${b.name}`, { description: b.desc, duration: 5000 });
        }
      }
    } catch {
      toast.error("Error submitting");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-sm uppercase font-bold text-neutral-500">Adaptive practice</div>
          <h1 className="text-4xl font-extrabold">Practice session</h1>
        </div>
        <div className="flex gap-2 items-center">
          <span className="nb-badge nb-tag-sun">Score: {correctCount}/{count}</span>
        </div>
      </div>

      {/* Subject filter */}
      <div className="flex flex-wrap gap-2">
        <button data-testid="subject-all" onClick={() => setSubject(null)} className={`px-4 py-2 rounded-full border-2 border-black font-bold text-sm ${!subject ? "bg-[var(--coral)] text-white" : "bg-white"}`}>All subjects</button>
        {subjects.map((s) => (
          <button key={s} data-testid={`subject-${s}`} onClick={() => setSubject(s)} className={`px-4 py-2 rounded-full border-2 border-black font-bold text-sm ${subject === s ? "bg-[var(--coral)] text-white" : "bg-white"}`}>{s}</button>
        ))}
      </div>

      {!q && !loading && (
        <div className="nb-card p-10 text-center">
          <Sparkles className="w-10 h-10 mx-auto text-[var(--coral)]" />
          <h2 className="text-3xl font-extrabold mt-3">Ready when you are</h2>
          <p className="text-neutral-600 mt-1">We'll pick a question that targets your weakest topic{subject ? ` in ${subject}` : ""}.</p>
          <button data-testid="start-practice-btn" onClick={next} className="nb-btn mt-6">
            Start <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading && !q && (
        <div className="nb-card p-10 text-center">
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-[var(--coral)]" />
          <p className="mt-3 font-bold">Crafting the perfect question…</p>
        </div>
      )}

      {q && (
        <div className="nb-card p-6 sm:p-8 space-y-5 pop-in">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <span className="nb-badge nb-tag-sky">{q.subject}</span>
              <span className="nb-badge nb-tag-sun">{q.topic}</span>
              <span className="nb-badge">Difficulty {q.difficulty}/5</span>
            </div>
          </div>
          <h2 data-testid="question-text" className="text-2xl font-extrabold leading-snug">{q.question}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {q.choices.map((c, i) => {
              const letter = "ABCD"[i];
              const isSel = selected === letter;
              const correct = result && result.correct_answer === letter;
              const wrong = result && !result.correct && isSel;
              return (
                <button
                  key={letter}
                  data-testid={`choice-${letter}`}
                  disabled={!!result}
                  onClick={() => setSelected(letter)}
                  className={`text-left p-4 border-2 border-black rounded-xl font-semibold transition-all ${
                    correct ? "bg-[var(--mint)]" : wrong ? "bg-red-300" : isSel ? "bg-[var(--sun)]" : "bg-white hover:bg-[var(--bg-highlight)]"
                  }`}
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-black text-white text-sm mr-3 font-bold">{letter}</span>
                  {c}
                </button>
              );
            })}
          </div>

          {result && (
            <div className={`p-4 border-2 border-black rounded-xl ${result.correct ? "bg-[var(--mint)]" : "bg-[var(--sun)]"}`}>
              <div className="flex items-center gap-2 font-extrabold text-lg">
                {result.correct ? <><CheckCircle2 className="w-6 h-6" /> Correct!</> : <><XCircle className="w-6 h-6" /> Answer: {result.correct_answer}</>}
              </div>
              <div className="mt-1 text-sm" data-testid="explanation">{result.explanation}</div>
            </div>
          )}

          <div className="flex gap-3">
            {!result ? (
              <button data-testid="submit-answer-btn" disabled={!selected || loading} onClick={submit} className="nb-btn">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : "Submit answer"}
              </button>
            ) : (
              <button data-testid="next-question-btn" onClick={next} disabled={loading} className="nb-btn nb-btn-accent">
                {loading ? "Loading…" : "Next question"} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
